import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import NatTokenRepo from '../../__NoAutomatedTests/components/token/repository/NatTokenRepo'
import {LOGIN_MODE, MESSAGE_TYPE, RECEIVING_AGENCY_CD, RESIDENT_USER_TYPE} from '../../CodeMaster'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {DisplayItemTitle} from '../../styledComponents/DisplayItem'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {Box} from '@mui/material'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {FlexGrowContent} from '../../styledComponents/FlexComponents'
import {OpenInNew} from '@mui/icons-material'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import NatResidentUserRepo from '../../__NoAutomatedTests/components/residentUser/repository/NatResidentUserRepo'
import {Messages} from '../../Messages'
import {
    ResidentLoginInfoSessionStorageKey,
} from '../../__NoAutomatedTests/common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ResidentAccountTransferContactUs} from './AccountTransferContactUs'
import {
    ApplyButton,
    BoldText,
    CompleteSampleArea,
    DescriptionArea,
    ListGridContainer,
    ListHeader,
    ListNumber,
    PcMarginLeft15,
    PcMarginLeft30,
    WhiteArea,
} from './AccountTransferCommon'
import {InlineLink} from '../../styledComponents/residentPage/Links'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faExternalLinkAlt} from '@fortawesome/free-solid-svg-icons'
import NatResidentMessageDialogComponent
    from '../../__NoAutomatedTests/components/common/resident/NatResidentMessageDialogComponent';

interface Props {
    tokenRepo: NatTokenRepo
    residentUserRepo: NatResidentUserRepo
    condominiumCd: string
    eventEmitter: EventEmitterExResident
}

interface State {
    token: string
    isSubUser: boolean
    isAdminUser: boolean
    mainUserName: string
    isLoadCompleted: boolean
    openDialog: boolean
}

class ResidentAccountTransferApplyToResonaPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        const userType = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_USER_RESIDENT_USER_TYPE)
        const isSubUser = userType === RESIDENT_USER_TYPE.SUB.value
        const isAdminUser = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_MODE) === LOGIN_MODE.ADMIN.value

        this.handleDialogOkClick = this.handleDialogOkClick.bind(this)
        this.handleApplyButtonClick = this.handleApplyButtonClick.bind(this)

        this.state = {
            token: '',
            isSubUser: isSubUser,
            isAdminUser: isAdminUser,
            mainUserName: '',
            isLoadCompleted: false,
            openDialog: false,
        }
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props
        const {isSubUser} = this.state

        await eventEmitter.showLoading()
        try {
            if (isSubUser) {
                await this.setupMainUserName()
            } else {
                await this.setupToken()
            }
            this.setState({
                isLoadCompleted: true,
            })
        } catch {
            return
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async setupToken(): Promise<void> {
        const {tokenRepo, navigate} = this.props

        const tokenResponse = await NatAsyncCallFunction(
            tokenRepo.getTokenAccountTransfer.bind(tokenRepo),
            navigate,
        )
        if (!tokenResponse) {
            throw Error('通信エラー')
        }
        this.setState({
            token: tokenResponse.details,
        })
    }

    async setupMainUserName(): Promise<void> {
        const {residentUserRepo, navigate} = this.props
        const residentUserResponse = await NatAsyncCallFunction(
            residentUserRepo.getResidentUserLogined.bind(residentUserRepo),
            navigate,
        )
        if (!residentUserResponse) {
            throw Error('通信エラー')
        }

        if (!residentUserResponse.details || !residentUserResponse.details.mainResidentUserInfo) {
            this.props.navigate(`/resident/error`, {state: {message: Messages.ESYCC0014.toString()}})
            throw Error('サブユーザーでログイン時にメインユーザー情報がない')
        }
        const {familyName, firstName} = residentUserResponse.details.mainResidentUserInfo

        this.setState({
            mainUserName: `${familyName} ${firstName}`,
        })
    }

    handleDialogOkClick(): void {
        this.setState({openDialog: false})
    }

    handleApplyButtonClick(event: any): void {
        if(this.state.isAdminUser) {
            event.preventDefault()
            this.setState({openDialog: true})
        }
    }

    render(): ReactNode {
        const {token, isSubUser, mainUserName, isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return null
        }

        return <>
            <NatResidentMessageDialogComponent
                open={this.state.openDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.INFORMATION,
                    message: Messages.ISYCR0001.toString(),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleDialogOkClick,
                        },
                    ],
                }}
            />
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>口座振替登録</StyledPageTitle>
            <PcMarginLeft15>
                <DescriptionArea>
                    {isSubUser && <>
                        <Box fontWeight={FontWeights.Bold} color={Colors.Alert}>
                            ご使用のアカウントはサブユーザのため、本機能をご利用いただけません。メインユーザの{mainUserName}さんにご依頼ください。
                        </Box>
                        <Box height={'20px'}/>
                    </>}
                    管理組合への毎月の支払い(管理費や施設利用料など)を、ご指定の口座より自動引落します。インターネット上で指定口座を登録・変更いただけます。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <div>指定口座には、個人名義の普通預金口座をご利用いただけます。利用可能な金融機関は
                            <InlineLink
                                href={'https://www.resona-ks.co.jp/netuser/pdf/web_teikeikinyukikan_ichiran.pdf'}
                                target={'_blank'}
                                rel={'noopener noreferrer'}
                            >
                                りそな決済サービスサイト&thinsp;
                                <FontAwesomeIcon icon={faExternalLinkAlt}/>
                            </InlineLink>
                            &thinsp;からご確認ください。
                        </div>
                        <div>・</div>
                        <div>毎月10日までに登録すると、翌月から自動引落が開始されます。</div>
                        <div>・</div>
                        <div>
                            <BoldText>管理組合へ直接お支払いする費用がない方は、本手続きは不要です。</BoldText>(賃貸契約でお住いの方など)
                        </div>
                    </ListGridContainer>
                </DescriptionArea>
            </PcMarginLeft15>

            <WhiteArea>
                <DisplayItemTitle>お手続き手順</DisplayItemTitle>
                <PcMarginLeft30>
                    <Box height={'20px'}/>
                    お手続きには、りそな決済サービスおよび金融機関が提供する外部サイトを利用します。
                    <Box height={'30px'}/>
                    <ListGridContainer>
                        <ListNumber>1</ListNumber>
                        <ListHeader>お手続き開始</ListHeader>
                        <div></div>
                        <div>「お手続きを開始する」ボタンより、口座振替登録を開始します。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>2</ListNumber>
                        <ListHeader>申請者情報入力</ListHeader>
                        <div></div>
                        <div>個人情報保護方針への同意、ならびに申請者情報(氏名など)を入力します。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>3</ListNumber>
                        <ListHeader>口座情報入力</ListHeader>
                        <div></div>
                        <div>口座情報(金融機関、支店名、口座番号など)を入力します。
                            <Box height={'3px'}/>
                            <Box fontSize={FontSize.Small} color={Colors.Body}>
                                <ListGridContainer>
                                    <div>※</div>
                                    <div>
                                        <span>インターネット上の手続きでは、農協・信用組合など一部ご利用いただけない金融機関がございます。</span>
                                        <span>お困りの際は、ページ下部の「本手続きのお問い合わせ先」までご連絡ください。</span>
                                    </div>
                                </ListGridContainer>
                            </Box>
                        </div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>4</ListNumber>
                        <ListHeader>本人確認</ListHeader>
                        <div></div>
                        <div>金融機関サイトにて、本人確認や認証を行います。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>5</ListNumber>
                        <ListHeader>お手続き完了</ListHeader>
                        <div></div>
                        <div>
                            下の画面が表示されましたら、お手続き完了です。
                            <Box height={'3px'}/>
                            <Box fontSize={FontSize.Small} color={Colors.Alert}>
                                <ListGridContainer>
                                    <div>※</div>
                                    <div>
                                        この画面が表示される前にページを閉じてしまうと、口座登録を完了できません。この画面が表示されたことを確認してからページを閉じてください。
                                    </div>
                                </ListGridContainer>
                            </Box>
                        </div>
                    </ListGridContainer>
                    <Box height={'30px'}/>
                </PcMarginLeft30>
                <CompleteSampleArea>
                    <img src={'/resources/images/icons/icon_reserve_complete.svg'} width={'60px'}
                         alt={'complete'}/>
                    <Box height={'15px'}/>
                    <Box fontWeight={FontWeights.Bold} color={Colors.AccentDark}>お手続きが完了しました</Box>
                    <Box fontSize={FontSize.Small}>このページを閉じてください</Box>
                </CompleteSampleArea>
                <ApplyButton
                    disabled={isSubUser}
                    to={`/account-transfer/${RECEIVING_AGENCY_CD.RESONA_KS.value}/registration/request?token=${token}`}
                    target={'_blank'}
                    onClick={this.handleApplyButtonClick}>
                    <FlexGrowContent>
                        <OpenInNew fontSize={'large'}/>
                    </FlexGrowContent>
                    <div>
                        お手続きを開始する
                        <Box fontWeight={FontWeights.Normal} fontSize={FontSize.Small} marginTop={'3px'}>
                            (外部サイトが新しい画面で開きます)
                        </Box>
                    </div>
                    <FlexGrowContent/>
                </ApplyButton>
                <PcMarginLeft30>
                    <ListGridContainer fontSize={FontSize.Small}>
                        <div>・</div>
                        <div>公衆Wi-Fiでは利用しないでください。個人情報や入力情報が盗み見られる危険性があります。</div>
                        <div>・</div>
                        <div>りそな決済サービスサイトや金融機関サイトのシステムメンテナンスのため、一時的にご利用できない場合があります。</div>
                    </ListGridContainer>
                </PcMarginLeft30>
            </WhiteArea>
            <Box height={'20px'}/>
            <ResidentAccountTransferContactUs/>
        </>
    }
}

export default createResidentPage(ResidentAccountTransferApplyToResonaPage)
